import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAccount, getReferral, checkout, deletePhotos, deleteAccount, getPricing, cancelSubscription, switchYearly } from "@/lib/pay";
import { cached, setCached } from "@/lib/me";
import { getConsent, setConsent } from "@/lib/analytics";
import { SubNav } from "@/components/revanche/SubNav";
import { CreditSheet } from "@/components/revanche/CreditSheet";
import { BottomSheet } from "@/components/revanche/BottomSheet";

const eur = (v) => `${Number(v).toFixed(2).replace(".", ",")} €`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch (e) { return ""; } };

const Compte = () => {
    const navigate = useNavigate();
    const [acc, setAcc] = useState(cached("account"));
    const [ref, setRef] = useState(cached("referral"));
    const [confirm, setConfirm] = useState(null);
    const [msg, setMsg] = useState("");
    const [pricing, setPricing] = useState(cached("pricing"));
    const [selectedPlan, setSelectedPlan] = useState("sub_yearly");
    const [creditOpen, setCreditOpen] = useState(false);
    const [retention, setRetention] = useState(false);
    const [working, setWorking] = useState(false);
    const [consent, setConsentChoice] = useState(getConsent());
    const changeConsent = (c) => { setConsent(c); setConsentChoice(c); setMsg(c === "granted" ? "Mesure d'audience activée." : "Mesure d'audience désactivée."); };

    const load = () => { getAccount().then((d) => { setAcc(d); setCached("account", d); }).catch(() => setAcc((a) => (a === null ? false : a))); };
    useEffect(() => {
        load();
        getReferral().then((d) => { setRef(d); setCached("referral", d); }).catch(() => {});
        getPricing().then((d) => { setPricing(d); setCached("pricing", d); }).catch(() => {});
        try { if (new URLSearchParams(window.location.search).get("retention") === "1") setRetention(true); } catch (e) {}
    }, []);

    const buy = async () => {
        try { const { checkout_url } = await checkout(selectedPlan); window.location.href = checkout_url; }
        catch (e) { setMsg("Paiement indisponible, réessaie."); }
    };
    const share = async () => {
        if (!ref) return;
        const txt = `Rejoins-moi sur Glow Up IA et découvre ton vrai score d'attirance : ${ref.link}`;
        if (navigator.share) { try { await navigator.share({ text: txt, url: ref.link }); } catch (e) {} }
        else { try { await navigator.clipboard.writeText(ref.link); setMsg("Lien copié"); } catch (e) {} }
    };
    const doDeletePhotos = async () => { try { await deletePhotos(); setMsg("Tes photos ont été supprimées."); } catch (e) { setMsg("Erreur, réessaie."); } setConfirm(null); };
    const doDeleteAccount = async () => { try { await deleteAccount(); } catch (e) {} localStorage.clear(); navigate("/"); };

    const doSwitchYearly = async () => {
        setWorking(true);
        try { await switchYearly(); setRetention(false); setMsg("Tu es passé à l'annuel. Bien joué."); load(); }
        catch (e) { setMsg("Bascule impossible, réessaie."); }
        finally { setWorking(false); }
    };
    const doCancel = async () => {
        setWorking(true);
        try { await cancelSubscription(); setRetention(false); setMsg("Ton abonnement est résilié."); load(); }
        catch (e) { setMsg("Résiliation impossible, réessaie."); }
        finally { setWorking(false); }
    };

    if (acc === false) {
        return (
            <div className="fixed inset-0 bg-black max-w-[480px] mx-auto flex flex-col" data-testid="compte-screen">
                <div className="flex-1 flex items-center justify-center px-6 text-center"><p className="text-white">Aucun compte. Termine ton analyse.</p></div>
                <SubNav />
            </div>
        );
    }
    if (!acc) return <div className="fixed inset-0 bg-black" />;
    const active = acc.sub_status === "active";

    return (
        <div className="fixed inset-0 bg-black max-w-[480px] mx-auto flex flex-col" data-testid="compte-screen">
            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-none px-5 py-6 safe-top">
                <h1 className="font-display font-black uppercase text-white text-3xl mb-5">Compte</h1>
                {msg && <div className="mb-4 rounded-lg px-4 py-2 text-sm text-white" style={{ border: "1px solid var(--border)", background: "var(--surface)" }} data-testid="compte-msg">{msg}</div>}

                {active && (
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="rounded-2xl p-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid="compte-analyses">
                            <div className="font-display text-white" style={{ fontSize: 40, fontWeight: 800, lineHeight: 1 }}>{acc.analyses_left}</div>
                            <div className="font-body mt-1" style={{ fontSize: 13, color: "var(--text-secondary)" }}>analyses restantes ce mois-ci</div>
                        </div>
                        <div className="rounded-2xl p-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid="compte-credits">
                            <div className="font-display text-white" style={{ fontSize: 40, fontWeight: 800, lineHeight: 1 }}>{acc.credits}</div>
                            <div className="font-body mt-1" style={{ fontSize: 13, color: "var(--text-secondary)" }}>crédits</div>
                        </div>
                    </div>
                )}

                {active && (
                    <button data-testid="compte-buy-credits" onClick={() => setCreditOpen(true)}
                        className="tap-feedback w-full h-14 rounded-2xl text-white font-body font-semibold mb-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                        Recharger des crédits
                    </button>
                )}

                {!active && pricing && (
                    <div className="rounded-2xl p-4 mb-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid="compte-offers">
                        <p className="text-white font-semibold mb-3">Formules</p>
                        <button data-testid="compte-monthly" onClick={() => setSelectedPlan("sub_monthly")}
                            className="tap-feedback w-full flex items-center justify-between rounded-xl p-3 text-white mb-2"
                            style={{ border: `2px solid ${selectedPlan === "sub_monthly" ? "var(--red)" : "var(--border)"}`, transition: "border-color 200ms var(--ease)" }}>
                            <span className="text-sm">Mensuel</span>
                            <span className="font-display font-black text-xl">{eur(pricing.monthly_first)}<span className="text-xs text-revtext-secondary font-body font-normal"> {pricing.offer_active ? "le 1er mois" : "/ mois"}</span></span>
                        </button>
                        <button data-testid="compte-yearly-card" onClick={() => setSelectedPlan("sub_yearly")}
                            className="tap-feedback w-full flex items-center justify-between rounded-xl p-3 text-white"
                            style={{ border: `2px solid ${selectedPlan === "sub_yearly" ? "var(--red)" : "var(--border)"}`, transition: "border-color 200ms var(--ease)" }}>
                            <span className="text-sm">Annuel</span>
                            <span className="font-display font-black text-xl" data-testid="compte-yearly-price">{eur(pricing.yearly)}<span className="text-xs text-revtext-secondary font-body font-normal"> / an</span></span>
                        </button>
                        <button data-testid="compte-choose-plan" onClick={buy}
                            className="tap-feedback mt-3 w-full h-12 rounded-full bg-revred text-white font-display font-black uppercase">
                            {selectedPlan === "sub_monthly" ? "Choisir le mensuel" : "Passer à l'annuel"}
                        </button>
                    </div>
                )}

                <div className="rounded-2xl p-4 mb-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                    <p className="text-white font-semibold">Invite 3 amis</p>
                    <p className="text-sm text-revtext-secondary mb-3">Amis invités : {ref?.invited ?? 0}/3 · dès que 3 s'inscrivent : +{ref?.bonus ?? 20} crédits</p>
                    <button data-testid="btn-share" onClick={share} className="tap-feedback w-full h-12 rounded-xl bg-revred text-white font-display font-black uppercase">Partager mon lien</button>
                </div>

                <div className="rounded-2xl p-4 mb-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid="compte-consent-card">
                    <p className="text-white font-semibold mb-1">Mesure d'audience</p>
                    <p className="text-[12px] text-revtext-secondary mb-3">Cookies Google Analytics et Microsoft Clarity. Choix modifiable à tout moment.</p>
                    <div className="flex gap-2">
                        <button data-testid="compte-consent-accept" onClick={() => changeConsent("granted")}
                            className="tap-feedback flex-1 rounded-full font-body" style={{ height: 40, fontSize: 13, border: `1px solid ${consent === "granted" ? "var(--red)" : "var(--border)"}`, color: "var(--text)" }}>
                            Accepter
                        </button>
                        <button data-testid="compte-consent-refuse" onClick={() => changeConsent("denied")}
                            className="tap-feedback flex-1 rounded-full font-body" style={{ height: 40, fontSize: 13, border: `1px solid ${consent === "denied" ? "var(--red)" : "var(--border)"}`, color: "var(--text)" }}>
                            Refuser
                        </button>
                    </div>
                </div>

                <div className="rounded-2xl p-4 mb-4" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                    <p className="text-white font-semibold mb-1">Mes données</p>
                    <p className="text-[12px] text-revtext-secondary mb-3">La suppression est définitive.</p>
                    <button data-testid="btn-delete-photos" onClick={() => setConfirm("photos")} className="tap-feedback w-full h-11 rounded-xl text-white mb-2" style={{ border: "1px solid var(--border)" }}>Supprimer mes photos</button>
                    <button data-testid="btn-delete-account" onClick={() => setConfirm("account")} className="tap-feedback w-full h-11 rounded-xl text-revred font-semibold" style={{ border: "1px solid var(--red)" }}>Supprimer mon compte</button>
                </div>

                {active && (
                    <div className="mt-2 mb-2 flex flex-col items-center gap-2">
                        {acc.renewal && (
                            <p data-testid="compte-renewal" className="font-body" style={{ fontSize: 13, color: "var(--text-secondary)" }}>
                                {acc.cancel_at_period_end ? "Fin d'accès le " : "Prochain renouvellement le "}{fmtDate(acc.renewal)}
                            </p>
                        )}
                        <button data-testid="btn-cancel-sub" onClick={() => setRetention(true)}
                            className="tap-feedback font-body" style={{ fontSize: 13, color: "var(--text-secondary)", textDecoration: "underline" }}>
                            Résilier mon abonnement
                        </button>
                    </div>
                )}

                <div className="mt-8 mb-4 text-center" data-testid="compte-legal-footer">
                    <p className="font-body" style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                        <a href="/mentions-legales" data-testid="footer-mentions" className="underline">Mentions légales</a>
                        {" · "}
                        <a href="/cgv" data-testid="footer-cgv" className="underline">CGV</a>
                        {" · "}
                        <a href="/confidentialite" data-testid="footer-confidentialite" className="underline">Confidentialité</a>
                    </p>
                    <p className="font-body mt-1" style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                        Contact : <a href="mailto:glowupia2026@gmail.com" data-testid="footer-contact" className="underline">glowupia2026@gmail.com</a>
                    </p>
                </div>
            </div>
            <SubNav />

            <CreditSheet open={creditOpen} onClose={() => setCreditOpen(false)} onPaid={() => { setCreditOpen(false); load(); }} />

            <BottomSheet open={retention} onClose={() => setRetention(false)} height="auto" testId="retention-sheet">
                <div className="px-6 pt-3 pb-6">
                    <h2 className="font-body font-bold text-white" style={{ fontSize: 24 }}>Avant de partir…</h2>
                    <p className="font-body mt-3" style={{ fontSize: 15, color: "var(--text-secondary)" }}>En résiliant, tu perds :</p>
                    <ul className="mt-2 flex flex-col gap-1.5 font-body" style={{ fontSize: 15, color: "#c8c8c8" }}>
                        <li>· Ton analyse complète et ton rapport</li>
                        <li>· Tes deux analyses incluses chaque mois</li>
                        <li>· L'accès au coach Mike et à tes plans</li>
                    </ul>
                    <div className="mt-5 rounded-2xl p-4" style={{ background: "var(--surface-2)", border: "2px solid var(--red)" }}>
                        <p className="font-body text-white font-semibold" style={{ fontSize: 15 }}>Reste et passe à l'annuel</p>
                        <p className="font-body mt-1" style={{ fontSize: 13, color: "var(--text-secondary)" }}>{pricing ? eur(pricing.yearly) : "99,99 €"} par an, soit près de deux mois offerts.</p>
                        <button data-testid="retention-switch" onClick={doSwitchYearly} disabled={working}
                            className="tap-feedback w-full mt-3 rounded-full text-white font-body font-semibold" style={{ height: 52, background: "var(--red)", opacity: working ? 0.5 : 1 }}>
                            Passer à l'annuel · {pricing ? eur(pricing.yearly) : "99,99 €"}/an
                        </button>
                    </div>
                    <button data-testid="retention-confirm" onClick={doCancel} disabled={working}
                        className="tap-feedback w-full mt-4 font-body" style={{ height: 44, fontSize: 14, color: "var(--text-secondary)", textDecoration: "underline" }}>
                        Confirmer la résiliation
                    </button>
                </div>
            </BottomSheet>

            {confirm && (
                <div className="absolute inset-0 bg-black/80 flex items-end sm:items-center justify-center p-5" data-testid="confirm-modal">
                    <div className="w-full max-w-[420px] rounded-2xl p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                        <p className="text-white font-display font-black uppercase text-xl">{confirm === "photos" ? "Supprimer tes photos ?" : "Supprimer ton compte ?"}</p>
                        <p className="text-sm text-revtext-secondary mt-2">{confirm === "photos" ? "Toutes tes photos seront définitivement effacées de nos serveurs." : "Ton compte, tes photos, tes analyses et tes plans seront définitivement supprimés."}</p>
                        <div className="grid grid-cols-2 gap-3 mt-5">
                            <button data-testid="confirm-cancel" onClick={() => setConfirm(null)} className="tap-feedback h-12 rounded-xl text-white" style={{ border: "1px solid var(--border)" }}>Annuler</button>
                            <button data-testid="confirm-ok" onClick={confirm === "photos" ? doDeletePhotos : doDeleteAccount} className="tap-feedback h-12 rounded-xl bg-revred text-white font-display font-black uppercase">Supprimer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Compte;
